<script lang="ts" setup>
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { watch } from "vue";
import useGroupPolicies from "../composables/useGrouṕPolices";
import EntryPoliciesModal from "./EntryPoliciesModal.vue";

interface Props {
  groupId: number | string;
  groupName: string;
  openModalPolicies: boolean;
}

const props = defineProps<Props>();

const { data, refetch, isLoading } = useGroupPolicies({
  params: {
    groupId: props.groupId,
  },
  immediate: false,
});

const emits = defineEmits(["openModalPolicies"]);

watch(
  () => props.openModalPolicies,
  (value) => {
    if (value) {
      refetch();
    }
  },
);
</script>

<template>
  <TransitionRoot appear :show="openModalPolicies" as="template">
    <Dialog as="div" class="relative z-50" @close="emits('openModalPolicies')">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden">
              <!-- Header -->
              <div class="bg-[#021F58] px-6 py-5 flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                    <span class="material-symbols-outlined text-white text-xl">policy</span>
                  </div>
                  <div>
                    <p class="text-xs text-white/60 font-medium uppercase tracking-widest">Políticas del grupo</p>
                    <DialogTitle as="h3" class="text-white font-bold text-lg leading-tight">
                      {{ groupName }}
                    </DialogTitle>
                  </div>
                </div>
                <button
                  @click="emits('openModalPolicies')"
                  class="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-colors"
                >
                  <span class="material-symbols-outlined text-white text-lg">close</span>
                </button>
              </div>

              <!-- Body -->
              <div class="px-6 py-6 max-h-[70vh] overflow-y-auto">
                <div v-if="!isLoading">
                  <EntryPoliciesModal :policies="data || []" />
                  <p v-if="!data?.length" class="text-center text-sm text-gray-400 py-8">
                    No hay políticas configuradas para este grupo.
                  </p>
                </div>

                <div v-else class="flex flex-col items-center justify-center py-16 gap-3">
                  <div class="w-10 h-10 border-[3px] border-[#021F58]/20 border-t-[#021F58] rounded-full animate-spin"></div>
                  <p class="text-sm text-gray-500">Cargando políticas...</p>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
